import React, { useEffect, useState } from "react";
import { FaSpinner, FaCheckCircle, FaTimesCircle } from "react-icons/fa";

interface Batch { id: number; name: string; isActive?: boolean; }
interface Assignment { id: number; classTestTitle: string; level: string; chapterRange: string; batchId: number; effectiveStatus: string; }
interface Attempt {
  id: number;
  studentId: number;
  studentName: string;
  lesenScore: number | null; lesenMaxScore: number | null;
  horenScore: number | null; horenMaxScore: number | null;
  schreibenScore: number | null; schreibenMaxScore: number | null;
  sprechenScore: number | null; sprechenMaxScore: number | null;
  totalScore: number | null; maxScore: number | null;
  percentage: number | null;
  passed: boolean | null;
  submittedAt: string | null;
  resultsPublished: boolean;
}

interface Props { apiBase?: string; }

const fmt = (s: number | null, m: number | null) =>
  s != null && m != null ? `${s}/${m}` : "—";

const AdminClassTestOverview: React.FC<Props> = ({ apiBase }) => {
  const base = apiBase ?? `${process.env.REACT_APP_API_BASE_URL}/api/admin/class-tests`;
  const isAdmin = base.includes("/admin/");

  const batchesUrl = isAdmin
    ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/batches`
    : `${process.env.REACT_APP_API_BASE_URL}/api/mentor/my-batches`;

  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  useEffect(() => {
    fetch(batchesUrl, { credentials: "include" })
      .then((r) => r.json())
      .then((data: Batch[]) => {
        const active = data.filter(b => b.isActive !== false);
        setBatches(active);
        if (active.length > 0) setSelectedBatchId(active[0].id);
      })
      .catch(console.error)
      .finally(() => setLoadingBatches(false));
  }, [batchesUrl]);

  useEffect(() => {
    if (!selectedBatchId) return;
    setLoadingAssignments(true);
    setAssignments([]);
    setSelectedAssignmentId(null);
    setAttempts([]);
    fetch(`${base}/assignments`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: Assignment[]) => {
        const filtered = data.filter((a) => a.batchId === selectedBatchId);
        setAssignments(filtered);
        if (filtered.length > 0) setSelectedAssignmentId(filtered[0].id);
      })
      .catch(console.error)
      .finally(() => setLoadingAssignments(false));
  }, [selectedBatchId, base]);

  useEffect(() => {
    if (!selectedAssignmentId) return;
    setLoadingAttempts(true);
    setAttempts([]);
    fetch(`${base}/assignments/${selectedAssignmentId}/attempts`, { credentials: "include" })
      .then((r) => r.json())
      .then(setAttempts)
      .catch(console.error)
      .finally(() => setLoadingAttempts(false));
  }, [selectedAssignmentId, base]);

  if (loadingBatches) {
    return <div className="flex justify-center py-24"><FaSpinner className="animate-spin text-4xl text-[#009fe3]" /></div>;
  }

  if (batches.length === 0) {
    return <div className="text-center py-16 text-gray-400 text-sm">No batches found.</div>;
  }

  const selected = assignments.find((a) => a.id === selectedAssignmentId);
  const passedCount = attempts.filter((a) => a.passed === true).length;
  const failedCount = attempts.filter((a) => a.passed === false).length;
  const avgPct = attempts.length > 0
    ? Math.round(attempts.reduce((s, a) => s + (a.percentage ?? 0), 0) / attempts.length)
    : null;

  return (
    <div className="space-y-4">
      {/* Selectors */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Batch</label>
          <select
            value={selectedBatchId ?? ""}
            onChange={(e) => setSelectedBatchId(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent min-w-[200px]"
          >
            {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        {loadingAssignments ? (
          <div className="flex items-end pb-2"><FaSpinner className="animate-spin text-[#009fe3]" /></div>
        ) : assignments.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Class Test</label>
            <select
              value={selectedAssignmentId ?? ""}
              onChange={(e) => setSelectedAssignmentId(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent min-w-[260px]"
            >
              {assignments.map((a) => (
                <option key={a.id} value={a.id}>
                  [{a.level}] {a.classTestTitle} — {a.chapterRange} ({a.effectiveStatus})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {assignments.length === 0 && !loadingAssignments && (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
          No class tests assigned to this batch.
        </div>
      )}

      {selected && !loadingAssignments && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Submitted", val: attempts.length, cls: "text-blue-700" },
              { label: "Passed", val: passedCount, cls: "text-green-700" },
              { label: "Failed", val: failedCount, cls: "text-red-700" },
              { label: "Avg Score", val: avgPct != null ? `${avgPct}%` : "—", cls: avgPct != null && avgPct >= 60 ? "text-green-700" : "text-red-600" },
            ].map(({ label, val, cls }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 text-center">
                <div className={`text-2xl font-bold ${cls}`}>{val}</div>
                <div className="text-xs text-gray-500 mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Table */}
          {loadingAttempts ? (
            <div className="flex justify-center py-12">
              <FaSpinner className="animate-spin text-3xl text-[#009fe3]" />
            </div>
          ) : attempts.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
              No submissions yet.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Student</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Lesen</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Hören</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Schreiben</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Sprechen</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {attempts.map((a) => {
                      const pct = a.percentage != null ? Math.round(a.percentage) : null;
                      return (
                        <tr
                          key={a.id}
                          className={
                            a.passed === true ? "bg-green-50/40 hover:bg-green-50" :
                            a.passed === false ? "bg-red-50/40 hover:bg-red-50" :
                            "hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">{a.studentName}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{fmt(a.lesenScore, a.lesenMaxScore)}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{fmt(a.horenScore, a.horenMaxScore)}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{fmt(a.schreibenScore, a.schreibenMaxScore)}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{fmt(a.sprechenScore, a.sprechenMaxScore)}</td>
                          <td className="px-4 py-3 text-center font-semibold text-gray-900">
                            {a.totalScore != null && a.maxScore != null
                              ? `${a.totalScore}/${a.maxScore} (${pct}%)`
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {a.passed === true ? (
                              <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                                <FaCheckCircle size={12} /> Pass
                              </span>
                            ) : a.passed === false ? (
                              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                <FaTimesCircle size={12} /> Fail
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">Pending</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminClassTestOverview;
